import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import {
  ACR_EDITOR_BASE_URL,
  ADD_NEW_PRODUCT_AUTOSAVE_STATUS,
  PRODUCT_UI_STATE_ADD_NEW,
  PUBLIC_EDITOR_MODE_MESSAGE,
  UNKNOWN_PRODUCT_AUTOSAVE_STATUS,
} from "../../../utlities/core.constants";
import applyPublicModeLifecycle from "../utils/publicModeLifecycle";
import { EvaluationContext } from "../stores/EvaluationContext";
import { CatalogContext } from "../stores/CatalogContext";
import { EditorNavigationProvider, addNewProductHasUnsavedContent } from "../stores/EditorNavigationContext";
import useCatalogApi from "../../../hooks/useCatalogApi.hook";
import transformCompleteCatalogToEditor from "../../../utils/transformCompleteCatalogToEditor";
import Navigation from "../components/Navigation";
import ReportProgressCard from "../components/ReportProgressCard";
import PageHeader from "../../PageHeader/PageHeader.component";
import { SectionContainer } from "../../../pages/shared.styled";
import useEditorAccess from "../../../hooks/useEditorAccess.hook";
import useFeatureFlag from "../../../hooks/useFeatureFlag.hook";
import { FEATURE_MY_DRAFTS } from "../../../utlities/featureFlags";
import "./AcrEditor.css";

/** Collapse a standard's full label to a short nav display name. */
const GROUP_LABEL_OVERRIDES = [
  { test: (l) => l.toUpperCase().includes("WCAG"), display: "WCAG Chapters" },
  { test: (l) => l.toUpperCase().includes("508"), display: "Section 508 Chapters" },
  { test: (l) => l.toUpperCase().includes("EN 301") || l.toUpperCase().includes("EN301"), display: "EN 301 549 Chapters" },
];

function getGroupNavLabel(standardLabel) {
  const label = standardLabel ?? "";
  return GROUP_LABEL_OVERRIDES.find((o) => o.test(label))?.display ?? label;
}

/**
 * ACR Editor layout: step nav + outlet for nested routes (chapter, about, report, etc.).
 * When evaluation.catalog is restored from localStorage but catalog data is missing (e.g. after refresh),
 * fetches the catalog from the API so chapters and the current route (e.g. Chapter Foo) work again.
 * Nav is always shown so the user can reach About even when restore fails.
 */
export default function AcrEditor() {
  const location = useLocation();
  const hasEditorAccess = useEditorAccess();
  const myDraftsEnabled = useFeatureFlag(FEATURE_MY_DRAFTS);
  const { evaluation, setEvaluation } = useContext(EvaluationContext);
  const { getCatalog, setCatalogData, catalogDataMap } = useContext(CatalogContext);
  const { getCompleteCatalogById, getConformanceLevels } = useCatalogApi();
  const [restoreError, setRestoreError] = useState(null);
  const [navIsStuck, setNavIsStuck] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return () => {};
    const observer = new IntersectionObserver(([entry]) => setNavIsStuck(!entry.isIntersecting));
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setEvaluation((prev) => {
      if (!prev?.report_save_status || prev.report_save_status === "idle") {
        return prev;
      }
      return {
        ...prev,
        report_save_status: "idle",
        report_save_error: undefined,
      };
    });
  }, [setEvaluation]);

  const catalogId = evaluation?.catalog;
  const catalogInMap = catalogId ? catalogDataMap[catalogId] : null;
  const needsRestore = Boolean(catalogId && !catalogInMap);
  const path = location.pathname.replace(/\/$/, "");
  const isOnAboutPage = path.endsWith("/about");
  // Base index (My Draft ACRs landing for authed users) and the Overview help page are both
  // informational: no report to autosave, no catalog to restore.
  const isOnOverviewPage = path.endsWith("/acr-editor") || path.endsWith("/overview");
  const isOnChapterOrReportPage = path.includes("/chapter") || path.includes("/report");

  // Clear error when catalog loads or when user navigates to About or Overview (so they can use the nav)
  useEffect(() => {
    if (catalogInMap || isOnAboutPage || isOnOverviewPage) setRestoreError(null);
  }, [catalogInMap, isOnAboutPage, isOnOverviewPage]);

  const restoreCatalog = useCallback(async () => {
    if (!catalogId || !setCatalogData) return;
    setRestoreError(null);
    try {
      const completeCatalog = await getCompleteCatalogById(catalogId);
      if (!completeCatalog?.standards?.length) {
        setRestoreError("Could not load catalog.");
        return;
      }
      const editorCatalog = transformCompleteCatalogToEditor(completeCatalog);
      const conformanceLevels = await getConformanceLevels();
      if (conformanceLevels.length > 0) editorCatalog.conformanceLevels = conformanceLevels;
      setCatalogData(catalogId, editorCatalog);
    } catch {
      setRestoreError("Could not load catalog.");
    }
  }, [catalogId, setCatalogData, getCompleteCatalogById, getConformanceLevels]);

  // Restore catalog when missing, but not while on About (user can pick a catalog there instead).
  // Runs for Public Mode users too, so a refreshed anonymous session keeps its chapters/report.
  useEffect(() => {
    if (needsRestore && !isOnAboutPage) restoreCatalog();
  }, [needsRestore, isOnAboutPage, restoreCatalog]);

  const catalog = useMemo(
    () => getCatalog(evaluation?.catalog),
    [getCatalog, evaluation?.catalog]
  );
  // Only show chapter groups when the selected catalog is actually loaded. When catalogId is set
  // but not yet in the map, we return no groups so the user can't navigate to a chapter until
  // the catalog is in the map (avoids wrong URL or "Chapter not found").
  const chapterGroups = useMemo(() => {
    if (catalogId && !catalogInMap) return [];
    if (!catalog?.standards?.length || !catalog?.chapters?.length) return [];
    const chapterMap = Object.fromEntries(catalog.chapters.map((ch) => [ch.id, ch]));
    // Merge standards that share the same nav label (e.g. WCAG 2.1 + WCAG 2.2 → one "WCAG" group)
    const groupMap = new Map();
    catalog.standards
      .filter((std) => std.chapters?.length)
      .forEach((std) => {
        const label = getGroupNavLabel(std.label);
        if (groupMap.has(label)) {
          const existing = groupMap.get(label);
          const existingIds = new Set(existing.chapters.map((ch) => ch.id));
          std.chapters.forEach((chId) => {
            if (!existingIds.has(chId)) {
              const ch = chapterMap[chId];
              if (ch) { existing.chapters.push(ch); existingIds.add(chId); }
            }
          });
        } else {
          groupMap.set(label, {
            id: std.id,
            label,
            chapters: std.chapters.map((chId) => chapterMap[chId]).filter(Boolean),
          });
        }
      });
    return Array.from(groupMap.values());
  }, [catalogId, catalogInMap, catalog]);
  // Anonymous / unauthorized users use the editor in Public Mode: no session to autosave to, so the
  // autosave slot carries a persistent Public Mode notice and the save-status wording never applies.
  const isPublicMode = !hasEditorAccess;

  // Public Mode ↔ authenticated download-only lifecycle. Owned here (the always-mounted editor
  // shell) rather than a single page so it holds no matter which route the author lands on — e.g.
  // returning to the Report page after logging in from "Log in to submit". Flags a Public Mode draft
  // as download-only, and graduates it into the normal Add-New-Product flow the instant access is
  // gained. A draft loaded later, while access is already granted (pendingRestore → restoreDraft),
  // is reconciled in restoreDraft — this effect only watches the access flip. See
  // applyPublicModeLifecycle for the (loop-safe) transition rules.
  useEffect(() => {
    setEvaluation((prev) => applyPublicModeLifecycle(prev, isPublicMode));
  }, [isPublicMode, setEvaluation]);

  const saveStatus = evaluation?.report_save_status || "idle";
  // Unknown products (not in the author's org) are download-only: autosave is intentionally off, so
  // the autosave slot shows a persistent "disabled" notice instead of a save status.
  const isUnknownProduct = Boolean(evaluation?.product_is_unknown);
  // An authenticated author with an unsaved new product (e.g. just graduated from Public Mode on
  // login): autosave/submit require a product that exists server-side, so explain why they're off
  // until the product is saved — on every editor page, not just the About form.
  const isAddNewProductPending =
    hasEditorAccess &&
    evaluation?.product_ui_state === PRODUCT_UI_STATE_ADD_NEW &&
    !evaluation?.product?.id &&
    addNewProductHasUnsavedContent(evaluation);
  // Tier-aware success wording: a real (org) product autosaves to the server, so "Draft saved";
  // download-only "unknown" products have no server autosave and rely on the on-device copy.
  let saveStatusText = "Draft saved";
  let saveStatusClassName = "usa-alert usa-alert--success usa-alert--slim";
  if (isPublicMode) {
    saveStatusText = PUBLIC_EDITOR_MODE_MESSAGE;
    saveStatusClassName = "usa-alert usa-alert--info usa-alert--slim";
  } else if (isUnknownProduct) {
    saveStatusText = UNKNOWN_PRODUCT_AUTOSAVE_STATUS;
    saveStatusClassName = "usa-alert usa-alert--info usa-alert--slim";
  } else if (isAddNewProductPending) {
    saveStatusText = ADD_NEW_PRODUCT_AUTOSAVE_STATUS;
    saveStatusClassName = "usa-alert usa-alert--info usa-alert--slim";
  } else if (saveStatus === "saving") {
    saveStatusText = "Saving...";
    saveStatusClassName = "usa-alert usa-alert--warning usa-alert--slim";
  } else if (saveStatus === "partial") {
    saveStatusText =
      evaluation?.report_save_error ||
      "Some information couldn't be saved due to formatting issues. Review the About page.";
    saveStatusClassName = "usa-alert usa-alert--warning usa-alert--slim";
  } else if (saveStatus === "failed") {
    saveStatusText =
      evaluation?.report_save_error ||
      "Autosave service cannot be reached right now. Please try again shortly. You can download your report to save progress.";
    saveStatusClassName = "usa-alert usa-alert--error usa-alert--slim";
  }
  // Show the banner whenever there's a save status to report, always for unknown products (where
  // autosave stays idle but we still want the "disabled" notice visible), and always in Public Mode.
  const showSaveStatusBanner =
    isPublicMode || isUnknownProduct || isAddNewProductPending || saveStatus !== "idle";

  return (
    <>
      <PageHeader
        imageUrl="/page-banners/VendorBannerImg.png"
        backgroundSize="1200px 180px"
        backgroundPosition="right"
        backgroundColor="var(--color-primary)"
      >
        ACR Editor
      </PageHeader>
      <EditorNavigationProvider>
        <div data-testid="acr-editor">
          <div ref={sentinelRef} aria-hidden="true" />
          <Navigation
            chapterGroups={chapterGroups}
            basePath={ACR_EDITOR_BASE_URL}
            isStuck={navIsStuck}
            showDraftsTab={hasEditorAccess && myDraftsEnabled}
          />
          <SectionContainer className="px-20">
            <div className="acr-editor-layout">
              <div className="acr-editor-content">
                {showSaveStatusBanner && (
                  <div
                    className={`${saveStatusClassName} mb-4`}
                    role={saveStatus === "failed" ? "alert" : "status"}
                    aria-live={saveStatus === "failed" ? "assertive" : "polite"}
                  >
                    <div className="usa-alert__body">
                      <p className="usa-alert__text">{saveStatusText}</p>
                    </div>
                  </div>
                )}
                {restoreError && !catalogInMap && isOnChapterOrReportPage && (
                  <p className="text-red-600 mb-4">
                    {restoreError} Select a catalog on the About page to continue.
                  </p>
                )}
                {needsRestore && !restoreError && isOnChapterOrReportPage && (
                  <p className="text-gray-600 mb-4">Loading catalog…</p>
                )}
                <Outlet />
              </div>
              <ReportProgressCard chapterGroups={chapterGroups} />
            </div>
          </SectionContainer>
        </div>
      </EditorNavigationProvider>
    </>
  );
}
